import { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { useAlerts } from '@/hooks/useAlerts';
import { useSites } from '@/hooks/useSites';
import { mockSiteAlerts, mockSites } from '@/data/mockData';
import { Bell, Plus, CheckCircle, AlertTriangle } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';

interface AlertsSectionProps {
  isDemo?: boolean;
  userRole?: string;
}

export function AlertsSection({ isDemo = false, userRole }: AlertsSectionProps) {
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState<'critical' | 'warning' | 'info'>('info');
  const [category, setCategory] = useState<'staff' | 'building' | 'system'>('staff');
  const [siteId, setSiteId] = useState<string>('all');
  const isSuperAdmin = userRole === 'super_admin';

  const { alerts: dbAlerts, createAlert, updateAlert } = useAlerts();
  const { sites: dbSites } = useSites();

  const alerts = isDemo ? mockSiteAlerts : dbAlerts;
  const sites = isDemo ? mockSites : dbSites;
  
  // Filter by role: staff only sees 'staff' and 'service' categories
  const isStaffRole = userRole === 'staff' || userRole === 'residential_aide';
  const activeAlerts = alerts.filter(a => a.status === 'active');
  const roleFilteredAlerts = isStaffRole
    ? activeAlerts.filter(a => !a.category || a.category === 'staff')
    : activeAlerts;

  // Group alerts by facility
  const alertsByFacility = useMemo(() => {
    const grouped: Record<string, any[]> = { general: [] };
    
    roleFilteredAlerts.forEach(alert => {
      const alertSiteId = (alert as any).site_id || 'general';
      if (!grouped[alertSiteId]) {
        grouped[alertSiteId] = [];
      }
      grouped[alertSiteId].push(alert);
    });
    
    // Remove empty groups except general
    Object.keys(grouped).forEach(key => {
      if (grouped[key].length === 0 && key !== 'general') {
        delete grouped[key];
      }
    });
    
    return grouped;
  }, [roleFilteredAlerts]);

  const handleCreate = () => {
    createAlert({
      title,
      description,
      type,
      category,
      clientId: undefined,
      siteId: siteId === 'all' ? undefined : siteId
    } as any);
    setIsCreateDialogOpen(false);
    setTitle('');
    setDescription('');
    setType('info');
    setCategory('staff');
    setSiteId('all');
  };

  const handleResolve = (alertId: string) => {
    updateAlert({ id: alertId, status: 'resolved' });
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold">Alerts</h2>
          <p className="text-xs text-muted-foreground">Manage system alerts</p>
        </div>
        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button size="sm">
              <Plus className="h-3 w-3 mr-1.5" />
              Create Alert
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create New Alert</DialogTitle>
              <DialogDescription>Add a new alert for your team</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label htmlFor="title" className="text-xs">Title *</Label>
                <Input
                  id="title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Alert title"
                  className="h-8 text-sm"
                />
              </div>
              <div>
                <Label htmlFor="description" className="text-xs">Description</Label>
                <Textarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Alert details"
                  className="min-h-16 text-sm"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Type *</Label>
                  <Select value={type} onValueChange={(v: any) => setType(v)}>
                    <SelectTrigger className="h-8 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="info">Info</SelectItem>
                      <SelectItem value="warning">Warning</SelectItem>
                      <SelectItem value="critical">Critical</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Category *</Label>
                  <Select value={category} onValueChange={(v: any) => setCategory(v)}>
                    <SelectTrigger className="h-8 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="staff">Staff</SelectItem>
                      <SelectItem value="system">System</SelectItem>
                      <SelectItem value="building">Building</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {isSuperAdmin && (
                <div>
                  <Label className="text-xs">Facility (Optional)</Label>
                  <Select value={siteId} onValueChange={setSiteId}>
                    <SelectTrigger className="h-8 text-sm">
                      <SelectValue placeholder="All Facilities" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">General (All)</SelectItem>
                      {sites.map((site: any) => (
                        <SelectItem key={site.id} value={site.id}>
                          {site.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" size="sm" onClick={() => setIsCreateDialogOpen(false)}>
                  Cancel
                </Button>
                <Button size="sm" onClick={handleCreate} disabled={!title}>
                  Create Alert
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {roleFilteredAlerts.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-8">
            <Bell className="h-8 w-8 text-muted-foreground mb-2" />
            <p className="text-sm text-muted-foreground">No active alerts</p>
          </CardContent>
        </Card>
      ) : (
        <Accordion type="multiple" className="space-y-2">
          {Object.entries(alertsByFacility).map(([siteId, facilityAlerts]) => {
            const matchedSite = sites.find((s: any) => s.id === siteId);
            const siteName = siteId === 'general' 
              ? 'General Alerts' 
              : matchedSite?.name || facilityAlerts[0]?.title?.split(' - ')[0] || 'Unassigned';
            
            return (
              <AccordionItem key={siteId} value={siteId} className="border rounded-lg">
                <AccordionTrigger className="px-3 py-2 hover:no-underline hover:bg-accent/50">
                  <div className="flex items-center justify-between w-full pr-2">
                    <span className="font-medium text-sm">{siteName}</span>
                    <Badge variant="secondary" className="text-xs">
                      {facilityAlerts.length} alert{facilityAlerts.length !== 1 ? 's' : ''}
                    </Badge>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-3 pb-2">
                  <div className="space-y-2">
                    {facilityAlerts.map((alert) => (
                      <Card key={alert.id} className="border-l-4" style={{
                        borderLeftColor: alert.type === 'critical' ? 'hsl(var(--destructive))' : 
                                       alert.type === 'warning' ? '#f59e0b' : '#3b82f6'
                      }}>
                        <CardContent className="p-3">
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <h3 className="font-medium text-sm">
                                  {(() => {
                                    // Strip facility name prefix (e.g. "Sleep Out - Near Capacity" → "Near Capacity")
                                    const matchedSite = sites.find((s: any) => s.id === (alert as any).site_id);
                                    const facilityName = matchedSite?.name;
                                    if (facilityName && alert.title.startsWith(facilityName + ' - ')) {
                                      return alert.title.slice(facilityName.length + 3);
                                    }
                                    // Also try splitting on " - " as fallback
                                    const parts = alert.title.split(' - ');
                                    if (parts.length > 1 && sites.some((s: any) => s.name === parts[0])) {
                                      return parts.slice(1).join(' - ');
                                    }
                                    return alert.title;
                                  })()}
                                </h3>
                                <Badge
                                  variant={
                                    alert.type === 'critical' ? 'destructive' :
                                    alert.type === 'warning' ? 'default' :
                                    'secondary'
                                  }
                                  className="text-xs px-1.5 py-0"
                                >
                                  {alert.type}
                                </Badge>
                                {alert.category && (
                                  <Badge variant="outline" className="text-xs px-1.5 py-0">
                                    {alert.category}
                                  </Badge>
                                )}
                              </div>
                              {alert.description && (
                                <p className="text-xs text-muted-foreground">{alert.description}</p>
                              )}
                              <p className="text-xs text-muted-foreground mt-1">
                                {new Date((alert as any).timestamp || (alert as any).created_at).toLocaleString()}
                              </p>
                            </div>
                            <div className="flex gap-1.5 flex-shrink-0">
                              <Button
                                size="sm"
                                variant="default"
                                onClick={() => handleResolve(alert.id)}
                                className="h-7 text-xs"
                              >
                                <CheckCircle className="h-3 w-3 mr-1" />
                                Resolve
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
      )}
    </div>
  );
}
